"use client";
import { createContext, useContext, useState, useRef, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { authenticatedApi } from "../lib/axios";

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return Cookies.get("auth_token") || null;
  });

  const router = useRouter();
  const queryClient = useQueryClient();

  // Prevent multiple logout calls
  const isLoggingOutRef = useRef(false);

  const login = (newToken: string) => {
    Cookies.set("auth_token", newToken, {
      expires: 7,
      path: "/",
      sameSite: "lax",
    });
    setToken(newToken);
    isLoggingOutRef.current = false; // reset on login
  };

  const logout = async () => {
    // Already logging out → ignore extra calls
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      // Clear local state first (so UI updates immediately)
      Cookies.remove("auth_token", { path: "/" });
      setToken(null);
      queryClient.clear();

      // Call logout API (optional – don't block redirect if it fails)
      try {
        await authenticatedApi.post("/logout");
      } catch (apiError) {
        console.error("Logout API failed:", apiError);
      }

      toast.success("Logged out successfully", {
        id: "logout-success",
        duration: 2500,
      });

      // Use replace instead of push to avoid history issues
      router.replace("/");
    } catch (error) {
      console.error("Logout error:", error);
      // Still force clean state
      Cookies.remove("auth_token", { path: "/" });
      setToken(null);
      queryClient.clear();
      router.replace("/");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated: !!token,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
